import * as React from 'react';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import theme from '../theme';
import {
  Alert, Box, Checkbox, Chip, CircularProgress, Divider, FormControl, FormControlLabel,
  IconButton, InputLabel, MenuItem, Paper, Select, Snackbar, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ImageIcon from '@mui/icons-material/Image';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import api from '../services/api';

// --- Custom Video Blot for Quill ---
const BlockEmbed = Quill.import('blots/block/embed');

class VideoBlot extends BlockEmbed {
  static create(url) {
    const node = super.create();
    node.setAttribute('src', url);
    node.setAttribute('controls', 'true');
    node.setAttribute('style', 'max-width:100%;border-radius:4px;');
    return node;
  }
  static value(node) {
    return node.getAttribute('src');
  }
}
VideoBlot.blotName = 'video';
VideoBlot.tagName = 'video';
Quill.register(VideoBlot, true);

// --- Image resize helper ---
function resizeImage(file, maxWidth = 1200) {
  return new Promise((resolve) => {
    const img = new Image();
    const reader = new FileReader();
    reader.onload = (e) => {
      img.onload = () => {
        if (img.width <= maxWidth) {
          // Still compress to JPEG if not transparent
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          canvas.getContext('2d').drawImage(img, 0, 0);
          const isPng = file.type === 'image/png';
          canvas.toBlob(resolve, isPng ? 'image/png' : 'image/jpeg', 0.8);
          return;
        }
        const ratio = maxWidth / img.width;
        const canvas = document.createElement('canvas');
        canvas.width = maxWidth;
        canvas.height = Math.round(img.height * ratio);
        canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
        const isPng = file.type === 'image/png';
        canvas.toBlob(resolve, isPng ? 'image/png' : 'image/jpeg', 0.8);
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
}

async function uploadToS3(ppiId, file) {
  const formData = new FormData();
  formData.append('file', file);
  const res = await api.post(`ppi/attachments/upload/${ppiId}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  const urlRes = await api.get(`ppi/attachments/url/${res.data.id}`);
  return { url: urlRes.data?.url, attachment: res.data };
}

async function uploadToS3Temp(file) {
  const formData = new FormData();
  formData.append('file', file);
  const res = await api.post('ppi/attachments/upload-temp/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return { attachment: res.data };
}

async function uploadInlineToS3(file) {
  const formData = new FormData();
  formData.append('file', file);
  const res = await api.post('ppi/attachments/upload-inline/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return `${api.defaults.baseURL}/ppi/attachments/media/?key=${encodeURIComponent(res.data.key)}`;
}

const isDark = theme.palette.mode === 'dark';

const quillDarkStyles = isDark ? `
  .ppi-quill .ql-toolbar.ql-snow {
    border-color: rgba(255,255,255,0.23) !important;
    background: #2a2a2a !important;
  }
  .ppi-quill .ql-toolbar .ql-stroke { stroke: #ccc !important; }
  .ppi-quill .ql-toolbar .ql-fill { fill: #ccc !important; }
  .ppi-quill .ql-toolbar .ql-picker-label { color: #ccc !important; }
  .ppi-quill .ql-toolbar .ql-picker-options { background: #333 !important; color: #eee !important; }
  .ppi-quill .ql-container.ql-snow {
    border-color: rgba(255,255,255,0.23) !important;
    background: #1e1e1e !important;
    color: #eee !important;
  }
  .ppi-quill .ql-editor { color: #eee !important; min-height: 120px; }
  .ppi-quill .ql-editor.ql-blank::before { color: #888 !important; }
  .ppi-quill .ql-snow .ql-picker { color: #ccc !important; }
  .ppi-quill .ql-toolbar button:hover .ql-stroke,
  .ppi-quill .ql-toolbar .ql-picker-label:hover .ql-stroke { stroke: #fff !important; }
  .ppi-quill .ql-toolbar button:hover .ql-fill { fill: #fff !important; }
  .ppi-quill .ql-toolbar button.ql-active .ql-stroke { stroke: #58a6ff !important; }
  .ppi-quill .ql-toolbar button.ql-active .ql-fill { fill: #58a6ff !important; }
` : '';

const SOURCE_OPTIONS = ['all', 'zabbix', 'wazuh'];
const FIELD_OPTIONS_BASE = ['name', 'hostname', 'ip', 'tags'];
const FIELD_OPTIONS_WAZUH = ['rule_id', 'srcip'];

export default function FormDialog(props) {
  const [handlerSnack, setHandlerSnack] = React.useState(false);
  const [empresas, setEmpresas] = React.useState([]);
  const [groups, setGroups] = React.useState([]);
  const [msgSnack, setMsgSnack] = React.useState('');
  const [sevSnack, setSevSnack] = React.useState('');

  const emptyItem = {
    cod_empresa: 1000,
    cod_ppi: '',
    action: '',
    group: '',
    description: '',
    weight: 100,
  };

  const [state, setState] = React.useState(emptyItem);
  const [steps, setSteps] = React.useState([]);
  const [rules, setRules] = React.useState([]);
  const [activeRuleGroup, setActiveRuleGroup] = React.useState(0);
  const [newRule, setNewRule] = React.useState({ source: 'all', field: 'name', pattern: '' });

  // Attachments
  const [attachments, setAttachments] = React.useState([]);
  const [pendingFiles, setPendingFiles] = React.useState([]);
  const [uploadLoading, setUploadLoading] = React.useState(false);

  // Quill refs for custom handlers
  const quillRefs = React.useRef({});

  const handleChange = (e) => {
    setState({ ...state, [e.target.name]: e.target.value });
  };

  React.useEffect(() => {
    if (!props.open) return;
    loadEditInfo();
    loadGroups();
    loadEmpresas();
  }, [props.id, props.open]);

  const loadEmpresas = () => {
    api.get('/empresa/all').then(res => setEmpresas(res.data)).catch(() => {});
  };

  const loadGroups = () => {
    api.get('/ppi/group/all').then(res => setGroups(res.data)).catch(() => {});
  };

  const loadEditInfo = () => {
    setState(emptyItem);
    setSteps([]);
    setRules([]);
    setAttachments([]);
    setPendingFiles([]);
    setActiveRuleGroup(0);
    if (!props.id) return;

    api.get(`ppi/ppi/find/${props.id}`)
      .then(res => {
        const data = res.data.__data__ || res.data;
        setState({
          cod_empresa: data.cod_empresa,
          cod_ppi: data.cod_ppi,
          action: data.action || '',
          group: data.group,
          description: data.description,
          weight: data.weight ?? 100,
        });
        if (data.steps?.length > 0) {
          setSteps(data.steps.map(s => ({
            order: s.order,
            title: s.title || '',
            content: s.content || '',
            required: s.required || false,
          })));
        }
        if (data.rules?.length > 0) {
          const loadedRules = data.rules.map(r => ({
            source: r.source,
            field: r.field,
            pattern: r.pattern,
            enabled: r.enabled ?? true,
            rule_group: r.rule_group ?? 0,
          }));
          setRules(loadedRules);
          const maxGroup = Math.max(...loadedRules.map(r => r.rule_group));
          setActiveRuleGroup(maxGroup);
        }
      })
      .catch(() => setState(emptyItem));

    // Load attachments
    api.get(`ppi/attachments/list/${props.id}`)
      .then(res => setAttachments(res.data || []))
      .catch(() => setAttachments([]));
  };

  const showNotify = (msg, sev = 'success') => {
    setMsgSnack(msg);
    setSevSnack(sev);
    setHandlerSnack(true);
  };

  // --- Steps ---
  const addStep = () => {
    setSteps(prev => [...prev, { order: prev.length + 1, title: '', content: '', required: false }]);
  };

  const removeStep = (idx) => {
    setSteps(prev => prev.filter((_, i) => i !== idx).map((s, i) => ({ ...s, order: i + 1 })));
  };

  const moveStep = (idx, dir) => {
    const arr = [...steps];
    const target = idx + dir;
    if (target < 0 || target >= arr.length) return;
    [arr[idx], arr[target]] = [arr[target], arr[idx]];
    setSteps(arr.map((s, i) => ({ ...s, order: i + 1 })));
  };

  const updateStep = (idx, field, value) => {
    setSteps(prev => prev.map((s, i) => i === idx ? { ...s, [field]: value } : s));
  };

  // --- Quill custom handlers ---
  const quillModules = React.useMemo(() => ({
    toolbar: {
      container: [
        [{ header: [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['link', 'image', 'video', 'code-block'],
        ['clean'],
      ],
      handlers: {
        image: function () {
          const input = document.createElement('input');
          input.setAttribute('type', 'file');
          input.setAttribute('accept', 'image/*');
          input.click();
          input.onchange = async () => {
            const file = input.files[0];
            if (!file) return;
            const quill = this.quill;
            const range = quill.getSelection(true);
            try {
              const resized = await resizeImage(file);
              const resizedFile = new File([resized], file.name, { type: resized.type });
              const url = await uploadInlineToS3(resizedFile);
              quill.insertEmbed(range.index, 'image', url);
              quill.setSelection(range.index + 1);
            } catch (err) {
              console.error('Image upload failed:', err);
            }
          };
        },
        video: function () {
          const input = document.createElement('input');
          input.setAttribute('type', 'file');
          input.setAttribute('accept', 'video/*');
          input.click();
          input.onchange = async () => {
            const file = input.files[0];
            if (!file) return;
            const quill = this.quill;
            const range = quill.getSelection(true);
            try {
              const url = await uploadInlineToS3(file);
              quill.insertEmbed(range.index, 'video', url);
              quill.setSelection(range.index + 1);
            } catch (err) {
              console.error('Video upload failed:', err);
            }
          };
        },
      },
    },
  }), []);

  // --- Rules ---
  const getRuleGroups = () => {
    const grouped = {};
    rules.forEach((r, idx) => {
      const g = r.rule_group ?? 0;
      if (!grouped[g]) grouped[g] = [];
      grouped[g].push({ ...r, _idx: idx });
    });
    return Object.keys(grouped).map(Number).sort((a, b) => a - b).map(gk => ({
      groupNum: gk,
      rules: grouped[gk],
    }));
  };

  const addRule = () => {
    if (!newRule.pattern) return;
    setRules(prev => [...prev, { ...newRule, rule_group: activeRuleGroup, enabled: true }]);
    setNewRule({ source: 'all', field: 'name', pattern: '' });
  };

  const removeRule = (idx) => {
    setRules(prev => {
      const updated = prev.filter((_, i) => i !== idx);
      // Reindex groups if any group became empty
      const usedGroups = [...new Set(updated.map(r => r.rule_group))].sort((a, b) => a - b);
      const groupMap = {};
      usedGroups.forEach((g, i) => { groupMap[g] = i; });
      const reindexed = updated.map(r => ({ ...r, rule_group: groupMap[r.rule_group] }));
      // Update activeRuleGroup
      const maxGroup = reindexed.length > 0 ? Math.max(...reindexed.map(r => r.rule_group)) : 0;
      setActiveRuleGroup(prev => Math.min(prev, maxGroup));
      return reindexed;
    });
  };

  const addNewRuleGroup = () => {
    const maxGroup = rules.length > 0 ? Math.max(...rules.map(r => r.rule_group)) : -1;
    setActiveRuleGroup(maxGroup + 1);
  };

  const getFieldOptions = () => {
    const base = [...FIELD_OPTIONS_BASE];
    if (newRule.source === 'wazuh' || newRule.source === 'all') {
      return [...base, ...FIELD_OPTIONS_WAZUH];
    }
    return base;
  };

  // --- Attachments ---
  const handleAddFiles = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) setPendingFiles(prev => [...prev, ...files]);
    e.target.value = '';
  };

  const removePendingFile = (idx) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const deleteAttachment = (attId) => {
    api.delete(`ppi/attachments/delete/${attId}`)
      .then(() => setAttachments(prev => prev.filter(a => a.id !== attId)))
      .catch(() => showNotify('Erro ao deletar anexo', 'error'));
  };

  const handleOpenAttachment = (att) => {
    if (att.s3_key) {
      window.open(`${api.defaults.baseURL}/ppi/attachments/media/?key=${encodeURIComponent(att.s3_key)}`, '_blank');
    } else {
      api.get(`ppi/attachments/url/${att.id}`)
        .then(res => { if (res.data?.url) window.open(res.data.url, '_blank'); })
        .catch(() => {});
    }
  };

  const getFileIcon = (contentType) => {
    if (!contentType) return <InsertDriveFileIcon fontSize="small" />;
    if (contentType.startsWith('image/')) return <ImageIcon fontSize="small" />;
    if (contentType.startsWith('video/')) return <VideoLibraryIcon fontSize="small" />;
    return <InsertDriveFileIcon fontSize="small" />;
  };

  // --- Submit ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...state,
      steps: steps.length > 0 ? steps : null,
      rules: rules.length > 0 ? rules : [],
    };

    const fail = () => showNotify('Um erro ocorreu...', 'error');

    try {
      let ppiId = props.id;
      if (!props.id) {
        const res = await api.post('ppi/ppi/create', payload);
        ppiId = res.data?.id || res.data;
      } else {
        await api.put(`ppi/ppi/edit/${props.id}/`, payload);
      }

      // Upload pending files (botao "Anexar")
      if (pendingFiles.length > 0 && ppiId) {
        setUploadLoading(true);
        const tempIds = [];
        for (const file of pendingFiles) {
          try {
            if (props.id) {
              await uploadToS3(ppiId, file);
            } else {
              const { attachment } = await uploadToS3Temp(file);
              tempIds.push(attachment.id);
            }
          } catch {
            console.error('Failed to upload:', file.name);
          }
        }
        if (!props.id && tempIds.length > 0) {
          await api.post('ppi/attachments/reparent/', { ppi_id: ppiId, attachment_ids: tempIds });
        }
        setUploadLoading(false);
      }

      showNotify(props.id ? 'Registro editado com sucesso.' : 'Registro adicionado com sucesso.');
      props.handlerClose();
    } catch {
      fail();
    }
  };

  const ruleGroups = getRuleGroups();
  // Determine all group numbers present (for display)
  const existingGroupNums = ruleGroups.map(g => g.groupNum);
  // If activeRuleGroup doesn't exist yet (new group), add it
  const allGroupNums = existingGroupNums.includes(activeRuleGroup)
    ? existingGroupNums
    : [...existingGroupNums, activeRuleGroup].sort((a, b) => a - b);

  return (
    <div>
      {isDark && <style>{quillDarkStyles}</style>}
      <Snack open={handlerSnack} message={msgSnack} severity={sevSnack} handlerSnack={() => setHandlerSnack(false)} />
      <Dialog open={props.open} fullWidth maxWidth='lg' scroll="paper">
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
          <DialogTitle>{!props.id ? 'Criar PPI' : 'Editar PPI'}</DialogTitle>
          <DialogContent dividers>
            {/* Basic fields */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
              <TextField
                required size="small" label="Descricao" name="description"
                value={state.description} onChange={handleChange}
                sx={{ flex: 2, minWidth: 200 }}
              />
              <TextField
                required size="small" label="Cod PPI" name="cod_ppi" type="number"
                value={state.cod_ppi} onChange={handleChange}
                sx={{ flex: 1, minWidth: 100 }}
              />
              <TextField
                size="small" label="Weight" name="weight" type="number"
                value={state.weight} onChange={handleChange}
                sx={{ width: 100 }}
                helperText="Menor = primeiro"
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <FormControl sx={{ flex: 1 }} size="small">
                <InputLabel>Empresa</InputLabel>
                <Select
                  required name="cod_empresa"
                  value={state.cod_empresa} onChange={handleChange}
                  label="Empresa"
                >
                  {empresas?.map(e => (
                    <MenuItem key={e.id} value={e.id}>{e.cod_empresa} - {e?.nome_fantasia}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl sx={{ flex: 1 }} size="small">
                <InputLabel>Grupo</InputLabel>
                <Select
                  required name="group"
                  value={state.group} onChange={handleChange}
                  label="Grupo"
                >
                  {groups?.map(g => (
                    <MenuItem key={g.id} value={g.id}>{g.description}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Steps */}
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" gutterBottom>Steps</Typography>
            {steps.map((step, idx) => (
              <Paper key={idx} variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Chip label={`Step ${idx + 1}`} size="small" color="primary" />
                  <TextField
                    size="small" label="Titulo" placeholder="Titulo do step"
                    value={step.title} onChange={(e) => updateStep(idx, 'title', e.target.value)}
                    sx={{ flex: 1 }}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={step.required}
                        onChange={(e) => updateStep(idx, 'required', e.target.checked)}
                        size="small"
                      />
                    }
                    label="Obrigatorio"
                  />
                  <IconButton size="small" onClick={() => moveStep(idx, -1)} disabled={idx === 0}>
                    <ArrowUpwardIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => moveStep(idx, 1)} disabled={idx === steps.length - 1}>
                    <ArrowDownwardIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" color="error" onClick={() => removeStep(idx)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
                <div className="ppi-quill">
                  <ReactQuill
                    theme="snow"
                    value={step.content}
                    onChange={(val) => updateStep(idx, 'content', val)}
                    modules={quillModules}
                    placeholder="Conteudo do step..."
                  />
                </div>
              </Paper>
            ))}
            <Button startIcon={<AddIcon />} onClick={addStep} size="small" variant="outlined">
              Adicionar Step
            </Button>

            {/* Match Rules */}
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" gutterBottom>Regras de Match</Typography>

            {allGroupNums.map((gk, gi) => {
              const group = ruleGroups.find(g => g.groupNum === gk);
              const groupRules = group ? group.rules : [];
              const isActive = gk === activeRuleGroup;

              return (
                <React.Fragment key={gk}>
                  {gi > 0 && (
                    <Typography
                      variant="caption"
                      sx={{ display: 'block', textAlign: 'center', my: 1, color: 'warning.main', fontWeight: 'bold', fontSize: '0.8rem' }}
                    >
                      — OU —
                    </Typography>
                  )}
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 1.5, mb: 0.5,
                      borderColor: isActive ? 'primary.main' : undefined,
                      cursor: 'pointer',
                    }}
                    onClick={() => setActiveRuleGroup(gk)}
                  >
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block', fontWeight: isActive ? 'bold' : 'normal' }}>
                      Condicao {gi + 1} — todas devem bater (AND)
                    </Typography>
                    {groupRules.map((r) => (
                      <Box key={r._idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Chip label={r.source} size="small" variant="outlined" />
                        <Chip label={r.field} size="small" />
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', flex: 1 }}>{r.pattern}</Typography>
                        <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); removeRule(r._idx); }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                    {groupRules.length === 0 && (
                      <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        Grupo vazio — adicione regras abaixo
                      </Typography>
                    )}

                    {/* Add rule form inside active group */}
                    {isActive && (
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1, pt: 1, borderTop: '1px dashed', borderColor: 'divider' }}>
                        <FormControl size="small" sx={{ width: 110 }}>
                          <InputLabel>Source</InputLabel>
                          <Select
                            value={newRule.source} label="Source"
                            onChange={(e) => setNewRule(prev => ({ ...prev, source: e.target.value }))}
                          >
                            {SOURCE_OPTIONS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                          </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ width: 130 }}>
                          <InputLabel>Campo</InputLabel>
                          <Select
                            value={newRule.field} label="Campo"
                            onChange={(e) => setNewRule(prev => ({ ...prev, field: e.target.value }))}
                          >
                            {getFieldOptions().map(f => <MenuItem key={f} value={f}>{f}</MenuItem>)}
                          </Select>
                        </FormControl>
                        <TextField
                          size="small" label="Regex pattern" value={newRule.pattern}
                          onChange={(e) => setNewRule(prev => ({ ...prev, pattern: e.target.value }))}
                          sx={{ flex: 1 }}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <Button size="small" variant="contained" onClick={(e) => { e.stopPropagation(); addRule(); }} startIcon={<AddIcon />}
                          disabled={!newRule.pattern}
                        >
                          Add
                        </Button>
                      </Box>
                    )}
                  </Paper>
                </React.Fragment>
              );
            })}

            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              <Button size="small" variant="outlined" onClick={addNewRuleGroup} startIcon={<AddIcon />}>
                Novo grupo (OU)
              </Button>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              Zabbix: name, hostname, ip, tags | Wazuh: + rule_id, srcip | Clique no grupo para adicionar regras
            </Typography>

            {/* Attachments */}
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" gutterBottom>
              <AttachFileIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
              Anexos
            </Typography>

            {/* Saved attachments */}
            {attachments.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                {attachments.map(att => (
                  <Chip
                    key={att.id}
                    icon={getFileIcon(att.content_type)}
                    label={att.filename}
                    size="small"
                    variant="outlined"
                    onClick={() => handleOpenAttachment(att)}
                    onDelete={() => deleteAttachment(att.id)}
                    deleteIcon={<DeleteIcon fontSize="small" />}
                    sx={{ cursor: 'pointer' }}
                  />
                ))}
              </Box>
            )}

            {/* Pending files */}
            {pendingFiles.length > 0 && (
              <Box sx={{ mb: 1 }}>
                <Typography variant="caption" color="text.secondary">Pendentes (upload ao salvar):</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                  {pendingFiles.map((file, idx) => (
                    <Chip
                      key={idx}
                      label={file.name}
                      size="small"
                      color="info"
                      variant="outlined"
                      onDelete={() => removePendingFile(idx)}
                    />
                  ))}
                </Box>
              </Box>
            )}

            <Button
              size="small" variant="outlined" component="label"
              startIcon={<AttachFileIcon />}
            >
              Anexar
              <input type="file" hidden multiple accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.txt" onChange={handleAddFiles} />
            </Button>
          </DialogContent>
          <DialogActions>
            <Button sx={{ color: theme.palette.text.primary }} onClick={props.handlerClose}>Cancelar</Button>
            <Button sx={{ color: theme.palette.text.primary }} type='submit' disabled={uploadLoading}>
              {uploadLoading ? <CircularProgress size={18} /> : (!props.id ? 'Adicionar' : 'Editar')}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </div>
  );
}

const Snack = (props) => (
  <Snackbar
    open={props.open}
    autoHideDuration={6000}
    onClose={() => props.handlerSnack()}
    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
  >
    <Alert onClose={() => props.handlerSnack()} severity={props.severity || 'error'} sx={{ width: '100%' }}>
      {props.message}
    </Alert>
  </Snackbar>
);
